import { useEffect, useState } from "react";
import { Phone, Mail, Instagram, Globe, MapPin, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxmYWNpYWwlMjBtYXNzYWdlJTIwc3BhJTIwd29tYW58ZW58MHx8fHwxNzc3Njg5OTYzfDA&ixlib=rb-4.1.0&q=85";

// QR code -> ambersbeauty.co.nz (transparent bg, deep burgundy fg, ECC=H for logo durability)
const QR_URL =
  "https://api.qrserver.com/v1/create-qr-code/?data=https%3A%2F%2Fambersbeauty.co.nz&size=600x600&margin=0&color=4A3737&bgcolor=FAEDE6&ecc=H&qzone=1";

const services = [
  { name: "Tailored Relaxing Facials", note: "Bespoke, calming, deeply nourishing" },
  { name: "LED Light Therapy", note: "Calm redness · brighten · restore" },
  { name: "Mesotherapy", note: "Targeted nutrient infusion for glow" },
  { name: "Chemical Peeling", note: "Resurface · refine · renew" },
  { name: "Microneedling", note: "Collagen induction for firmer skin" },
];

const CropMarks = () => (
  <>
    <span className="crop-mark cm-tl-h" />
    <span className="crop-mark cm-tl-v" />
    <span className="crop-mark cm-tr-h" />
    <span className="crop-mark cm-tr-v" />
    <span className="crop-mark cm-bl-h" />
    <span className="crop-mark cm-bl-v" />
    <span className="crop-mark cm-br-h" />
    <span className="crop-mark cm-br-v" />
  </>
);

export default function Leaflet() {
  const [bleed, setBleed] = useState(false);

  useEffect(() => {
    document.title = "Amber's Mobile Beauty · A5 Leaflet";
  }, []);

  // Dynamically swap @page size based on variant (CSS @page can't be class-scoped)
  useEffect(() => {
    const id = "leaflet-page-size";
    let style = document.getElementById(id);
    if (!style) {
      style = document.createElement("style");
      style.id = id;
      document.head.appendChild(style);
    }
    style.textContent = bleed
      ? "@page { size: 154mm 216mm; margin: 0; }"
      : "@page { size: A5 portrait; margin: 0; }";
    return () => {
      // keep style around between toggles; remove only on unmount
      if (style && style.parentNode) style.parentNode.removeChild(style);
    };
  }, [bleed]);

  // Hide the platform "Made with Emergent" badge while on the leaflet page
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    let prev = null;
    if (badge) {
      prev = badge.style.cssText;
      badge.style.setProperty("display", "none", "important");
    }
    return () => {
      if (badge && prev !== null) badge.style.cssText = prev;
    };
  }, []);

  return (
    <div data-testid="leaflet-page" className={`leaflet-shell ${bleed ? "leaflet-bleed" : ""}`}>
      <div className="leaflet-toolbar print:hidden">
        <div className="mx-auto max-w-[1400px] px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <span className="font-serif-display text-[#4A3737] text-xl">
              Amber's Mobile Beauty
            </span>
            <span className="text-[10px] tracking-[0.32em] uppercase text-[#B78782]">
              {bleed
                ? "Print-Ready · 154 × 216 mm (A5 + 3 mm bleed + crop marks)"
                : "A5 Print Leaflet · 148 × 210 mm"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              role="tablist"
              aria-label="Leaflet variant"
              className="inline-flex border border-[#B78782]/40 rounded-full p-1 bg-white/40 backdrop-blur"
            >
              <button
                role="tab"
                aria-selected={!bleed}
                data-testid="leaflet-variant-standard"
                onClick={() => setBleed(false)}
                className={`text-[10px] tracking-[0.28em] uppercase px-4 py-2 rounded-full transition-colors ${
                  !bleed
                    ? "bg-[#B78782] text-[#FAEDE6]"
                    : "text-[#4A3737] hover:bg-[#B78782]/10"
                }`}
              >
                Standard
              </button>
              <button
                role="tab"
                aria-selected={bleed}
                data-testid="leaflet-variant-bleed"
                onClick={() => setBleed(true)}
                className={`text-[10px] tracking-[0.28em] uppercase px-4 py-2 rounded-full transition-colors ${
                  bleed
                    ? "bg-[#B78782] text-[#FAEDE6]"
                    : "text-[#4A3737] hover:bg-[#B78782]/10"
                }`}
              >
                + Bleed
              </button>
            </div>
            <a
              href="/"
              data-testid="leaflet-back-btn"
              className="text-[11px] tracking-[0.28em] uppercase border border-[#B78782] text-[#4A3737] px-4 py-2 hover:bg-[#B78782]/10 transition-colors"
            >
              ← Site
            </a>
            <a
              href={
                bleed
                  ? "/print/ambers-mobile-beauty-leaflet-A5-bleed.pdf"
                  : "/print/ambers-mobile-beauty-leaflet-A5.pdf"
              }
              download
              data-testid="leaflet-download-pdf"
              className="text-[11px] tracking-[0.28em] uppercase border border-[#B78782] text-[#4A3737] px-4 py-2 hover:bg-[#B78782]/10 transition-colors"
            >
              Download PDF
            </a>
            <button
              data-testid="leaflet-print-btn"
              onClick={() => window.print()}
              className="text-[11px] tracking-[0.28em] uppercase bg-[#B78782] text-[#FAEDE6] px-5 py-2.5 hover:bg-[#C5A8A4] transition-colors"
            >
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      <div className="leaflet-stage">
        {/* ============== FRONT PAGE ============== */}
        <article
          data-testid="leaflet-front"
          className="leaflet-page leaflet-front"
          aria-label="Leaflet front"
        >
          <div className="lf-hero">
            <img src={HERO_IMG} alt="" className="lf-hero-img" />
            <div className="lf-hero-overlay" />
            <div className="lf-hero-content">
              <div className="lf-eyebrow">
                <span className="lf-rule" />
                <span>Auckland Mobile Beauty</span>
              </div>
              <h1 className="lf-title">
                Beauty
                <br />
                that comes
                <br />
                <em className="lf-script">to&nbsp;you.</em>
              </h1>
            </div>
          </div>

          <div className="lf-bottom">
            <p className="lf-intro">
              Luxury facials, LED therapy, mesotherapy, microneedling and
              chemical peels — quietly delivered in the comfort of your own
              home, anywhere across Auckland.
            </p>

            <div className="lf-offer">
              <div className="lf-offer-badge">
                <Sparkles size={14} strokeWidth={1.4} className="lf-icon-rose" />
                <span>New Client Offer</span>
              </div>
              <p className="lf-offer-headline">
                <em className="lf-script-sm">Enjoy</em>{" "}
                <strong>15% off</strong> your first treatment
              </p>
              <p className="lf-offer-fine">
                Mention this leaflet when you enquire. Valid for new clients,
                Auckland-wide. By appointment.
              </p>
            </div>

            <div className="lf-foot-row">
              <div className="lf-foot-brand">
                <span className="font-serif-display lf-foot-name">
                  Amber's Mobile Beauty
                </span>
                <span className="lf-script-tag">Beauty that comes to you</span>
                <span className="lf-foot-call">
                  <Phone size={11} strokeWidth={1.5} className="lf-icon-rose" />
                  <span>021 109 7531</span>
                </span>
                <span className="lf-foot-handle">@ambers_mobilebeauty</span>
              </div>
              <div className="lf-qr">
                <img src={QR_URL} alt="QR code linking to ambersbeauty.co.nz" className="lf-qr-img" />
                <span className="lf-qr-label">
                  Scan to book
                  <span className="lf-qr-domain">ambersbeauty.co.nz</span>
                </span>
              </div>
            </div>
          </div>

          {bleed && <CropMarks />}
        </article>

        {/* ============== BACK PAGE ============== */}
        <article
          data-testid="leaflet-back"
          className="leaflet-page leaflet-back"
          aria-label="Leaflet back"
        >
          <header className="lb-head">
            <div>
              <p className="lf-eyebrow lf-eyebrow-dark">
                <span className="lf-rule lf-rule-rose" />
                <span>The Treatment Menu</span>
              </p>
              <h2 className="lb-title">
                Mobile treatments,
                <br />
                <em className="lf-script-sm">refined.</em>
              </h2>
            </div>
            <div className="lb-stamp">
              <span>EST.</span>
              <span className="lb-stamp-year">Auckland</span>
              <span>NZ</span>
            </div>
          </header>

          <ul className="lb-services">
            {services.map((s, i) => (
              <li key={s.name} className="lb-service">
                <span className="lb-num">{String(i + 1).padStart(2, "0")}</span>
                <div className="lb-service-text">
                  <p className="lb-service-name">{s.name}</p>
                  <p className="lb-service-note">{s.note}</p>
                </div>
                <span className="lb-dots" aria-hidden />
                <span className="lb-price">On enquiry</span>
              </li>
            ))}
          </ul>

          <section className="lb-why">
            <p className="lb-why-quote">
              <em className="lf-script-sm">"</em>
              I bring the studio to your door — so the only thing left for you
              to do is exhale.
              <em className="lf-script-sm">"</em>
            </p>
            <p className="lb-why-attr">— Amber Rufer · Mobile Beauty Specialist</p>
          </section>

          <section className="lb-contact">
            <p className="lf-eyebrow lf-eyebrow-dark lb-contact-eyebrow">
              <span className="lf-rule lf-rule-rose" />
              <span>Enquire</span>
            </p>
            <div className="lb-contact-grid">
              <div className="lb-c">
                <Phone size={12} strokeWidth={1.5} className="lf-icon-rose" />
                <div>
                  <span className="lb-c-label">Phone / Text</span>
                  <span className="lb-c-value">021 109 7531</span>
                </div>
              </div>
              <div className="lb-c">
                <Mail size={12} strokeWidth={1.5} className="lf-icon-rose" />
                <div>
                  <span className="lb-c-label">Email</span>
                  <span className="lb-c-value">amber.rufer@gmail.com</span>
                </div>
              </div>
              <div className="lb-c">
                <Instagram size={12} strokeWidth={1.5} className="lf-icon-rose" />
                <div>
                  <span className="lb-c-label">Instagram</span>
                  <span className="lb-c-value">@ambers_mobilebeauty</span>
                </div>
              </div>
              <div className="lb-c">
                <Globe size={12} strokeWidth={1.5} className="lf-icon-rose" />
                <div>
                  <span className="lb-c-label">Website</span>
                  <span className="lb-c-value">ambersbeauty.co.nz</span>
                </div>
              </div>
              <div className="lb-c lb-c-wide">
                <MapPin size={12} strokeWidth={1.5} className="lf-icon-rose" />
                <div>
                  <span className="lb-c-label">Service Area</span>
                  <span className="lb-c-value">
                    Auckland-wide · in the comfort of your home
                  </span>
                </div>
              </div>
            </div>
          </section>

          <footer className="lb-foot">
            <span>LUXURY MOBILE BEAUTY · IN THE COMFORT OF YOUR HOME</span>
            <span className="lf-heart">♥</span>
          </footer>

          {bleed && <CropMarks />}
        </article>
      </div>
    </div>
  );
}
